import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
 
import { Filter, X } from "lucide-react";
import api from "@/utils/api";
import useDashboardFilters from "@/hooks/useDashboardFilters";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const STATUS_OPTIONS = [
  { value: "all", label: "Alle Rechnungsstatus" },
  { value: "created", label: "Erstellt" },
  { value: "sent", label: "Gesendet" },
  { value: "paid", label: "Bezahlt" },
  { value: "cancelled", label: "Storniert" },
];

const PRESET_OPTIONS = [
  { value: "today", label: "Heute" },
  { value: "this-week", label: "Diese Woche" },
  { value: "last-30-days", label: "Letzte 30 Tage" },
  { value: "this-month", label: "Dieser Monat" },
  { value: "this-quarter", label: "Dieses Quartal" },
  { value: "this-year", label: "Dieses Jahr" },
  { value: "custom", label: "Individueller Zeitraum" },
];

const buildLabel = (label, count) =>
  count ? `${label} (${count})` : label;

export default function DashboardFilters({
  title = "Dashboard Filter",
  subtitle = "Filtere deine Daten in Echtzeit",
  showProjects = true,
  showCustomers = true,
  showStatus = false,
}) {
  const {
    filters,
    setFilters,
    setPreset,
    resetFilters,
    hasActiveFilters,
  } = useDashboardFilters();

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const response = await api.get("/projects");
      return response.data;
    },
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const response = await api.get("/customers");
      return response.data;
    },
  });

  const selectedProjects = useMemo(
    () => projects.filter((project) => filters.projectIds.includes(project.id)),
    [projects, filters.projectIds]
  );

  const selectedCustomers = useMemo(
    () => customers.filter((customer) => filters.customerIds.includes(customer.id)),
    [customers, filters.customerIds]
  );

  const dateRangeLabel = useMemo(() => {
    if (!filters.dateFrom || !filters.dateTo) return "Zeitraum wählen";
    return `${filters.dateFrom} → ${filters.dateTo}`;
  }, [filters.dateFrom, filters.dateTo]);

  const toggleSelection = (current, id) =>
    current.includes(id) ? current.filter((value) => value !== id) : [...current, id];

  const removeProject = (id) =>
    setFilters({ projectIds: filters.projectIds.filter((value) => value !== id) });

  const removeCustomer = (id) =>
    setFilters({ customerIds: filters.customerIds.filter((value) => value !== id) });

  const removeStatus = () => setFilters({ status: "all" });

  const updateCustomRange = (partial) => {
    const nextRange = {
      dateFrom: partial.dateFrom ?? filters.dateFrom,
      dateTo: partial.dateTo ?? filters.dateTo,
    };
    setFilters({
      datePreset: "custom",
      ...nextRange,
    });
  };

  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm space-y-4" data-testid="dashboard-filters">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <Filter className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
        </div>
        <Button
          variant="outline"
          className="w-full md:w-auto"
          onClick={resetFilters}
          disabled={!hasActiveFilters}
          data-testid="filters-reset"
        >
          Alle Filter zurücksetzen
        </Button>
      </div>

      <div className={`grid grid-cols-1 gap-3 ${showStatus ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
        {showProjects && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="justify-between"
                data-testid="filter-projects-trigger"
              >
                {buildLabel("Projekte", filters.projectIds.length)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64">
              {projects.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">Keine Projekte</div>
              ) : (
                projects.map((project) => (
                  <DropdownMenuCheckboxItem
                    key={project.id}
                    checked={filters.projectIds.includes(project.id)}
                    onCheckedChange={() =>
                      setFilters({
                        projectIds: toggleSelection(filters.projectIds, project.id),
                      })
                    }
                    data-testid={`filter-project-${project.id}`}
                  >
                    {project.name}
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {showCustomers && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="justify-between"
                data-testid="filter-customers-trigger"
              >
                {buildLabel("Kunden", filters.customerIds.length)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64">
              {customers.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">Keine Kunden</div>
              ) : (
                customers.map((customer) => (
                  <DropdownMenuCheckboxItem
                    key={customer.id}
                    checked={filters.customerIds.includes(customer.id)}
                    onCheckedChange={() =>
                      setFilters({
                        customerIds: toggleSelection(filters.customerIds, customer.id),
                      })
                    }
                    data-testid={`filter-customer-${customer.id}`}
                  >
                    {customer.name}
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <div className="flex flex-col gap-2">
          <Select
            value={filters.datePreset}
            onValueChange={(value) =>
              value === "custom"
                ? setFilters({ datePreset: "custom" })
                : setPreset(value)
            }
          >
            <SelectTrigger data-testid="filter-date-preset">
              <SelectValue placeholder="Zeitraum wählen" />
            </SelectTrigger>
            <SelectContent>
              {PRESET_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-testid={`filter-date-preset-option-${option.value}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filters.datePreset === "custom" && (
            <div className="grid grid-cols-2 gap-2" data-testid="filter-date-range-fields">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Von</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(event) =>
                    updateCustomRange({ dateFrom: event.target.value })
                  }
                  data-testid="filter-date-from"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Bis</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(event) =>
                    updateCustomRange({ dateTo: event.target.value })
                  }
                  data-testid="filter-date-to"
                />
              </div>
            </div>
          )}
        </div>
        {showStatus && (
          <Select value={filters.status} onValueChange={(value) => setFilters({ status: value })}>
            <SelectTrigger data-testid="filter-status">
              <SelectValue placeholder="Rechnungsstatus" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-testid={`filter-status-option-${option.value}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="filter-chips">
        {showProjects &&
          selectedProjects.map((project) => (
            <Badge key={project.id} variant="secondary" className="gap-1">
              {project.name}
              <button
                onClick={() => removeProject(project.id)}
                className="text-gray-500 hover:text-gray-700"
                data-testid={`filter-chip-project-${project.id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        {showCustomers &&
          selectedCustomers.map((customer) => (
            <Badge key={customer.id} variant="secondary" className="gap-1">
              {customer.name}
              <button
                onClick={() => removeCustomer(customer.id)}
                className="text-gray-500 hover:text-gray-700"
                data-testid={`filter-chip-customer-${customer.id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        {showStatus && filters.status !== "all" && (
          <Badge variant="secondary" className="gap-1">
            Rechnungsstatus: {STATUS_OPTIONS.find((option) => option.value === filters.status)?.label}
            <button
              onClick={removeStatus}
              className="text-gray-500 hover:text-gray-700"
              data-testid="filter-chip-status"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {filters.datePreset === "custom" && (
          <Badge variant="secondary" className="gap-1">
            Zeitraum: {dateRangeLabel}
            <button
              onClick={() => setPreset("last-30-days")}
              className="text-gray-500 hover:text-gray-700"
              data-testid="filter-chip-date-range"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
      </div>
    </div>
  );
}